import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ApexAxisChartSeries } from 'ng-apexcharts';
import { Graficas } from '../../../../graficas/graficas';
import { RegistroSignosVitales } from '../registro-signos-vitales';
import { Atencion, Paciente, SignosVitales } from '../../pacientes';
import { PacienteService } from '../../../../core/services/pacientes.service';
import { AtencionService } from '../../../../core/services/atencion.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-graficas-signos-vitales',
  standalone: true,

  imports: [MatButtonModule, MatIconModule, MatMenuModule, Graficas],

  templateUrl: './graficas-signos-vitales.html',
  styleUrl: './graficas-signos-vitales.scss',
})
export class GraficasSignosVitales {
  constructor(
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<GraficasSignosVitales>,
    private pacienteService: PacienteService,
    private atencionService: AtencionService,
    @Inject(MAT_DIALOG_DATA)
    public paciente: Paciente,
  ) {}

  fechaActual: Date = new Date();

  atenciones: Atencion[] = [];

  cargando = true;

  // CALCULAR EDAD
  obtenerEdad(): string {
    const fechaNacimiento = this.paciente.fechaNacimiento;

    if (!fechaNacimiento) {
      return '';
    }

    const nacimiento = new Date(fechaNacimiento);
    const hoy = new Date();

    let anios = hoy.getFullYear() - nacimiento.getFullYear();
    let meses = hoy.getMonth() - nacimiento.getMonth();
    let dias = hoy.getDate() - nacimiento.getDate();

    if (dias < 0) {
      meses--;
      const mesAnterior = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
      dias += mesAnterior.getDate();
    }

    if (meses < 0) {
      anios--;
      meses += 12;
    }

    if (anios < 0) {
      return '';
    }

    if (anios === 0 && meses === 0) {
      return dias === 1 ? '1 día' : `${dias} días`;
    }

    if (anios === 0) {
      return meses === 1 ? '1 mes' : `${meses} meses`;
    }

    if (anios <= 10) {
      const textoAnios = anios === 1 ? '1 año' : `${anios} años`;
      if (meses === 0) return textoAnios;
      const textoMeses = meses === 1 ? '1 mes' : `${meses} meses`;
      return `${textoAnios} y ${textoMeses}`;
    }

    return anios === 1 ? '1 año' : `${anios} años`;
  }

  // =====================================================
  // CONFIGURACIÓN
  // =====================================================

  titulo = 'Peso';

  unidad = 'kg';

  tipo: 'line' | 'bar' | 'scatter' | 'area' = 'line';

  colores = ['#2F6F73'];

  series: ApexAxisChartSeries = [];

  // =====================================================
  // INICIALIZACIÓN
  // =====================================================

  ngOnInit(): void {
    // console.log('========== GRÁFICAS ==========');

    // console.log('Paciente:', this.paciente);

    // console.log('Signos vitales:', this.paciente.id);

    this.cargarAtenciones();
  }

  cargarAtenciones(): void {
    this.cargando = true;

    // Validar que tengamos el ID del paciente
    if (!this.paciente?.id) {
      // console.error('No se encontró el ID del paciente');

      this.atenciones = [];
      this.series = [];
      this.cargando = false;

      return;
    }

    // console.log('========== CONSULTANDO ATENCIONES ==========');

    // console.log('Paciente ID:', this.paciente.id);

    this.atencionService.obtenerAtenciones(this.paciente.id).subscribe({
      next: (atenciones) => {
        // console.log('========== ATENCIONES DEL PACIENTE ==========');

        // console.log(atenciones);

        // console.log(
        //   'OBSERVACIONES:',
        //   atenciones.map((a) => ({
        //     fecha: a.signosVitales?.fechaRegistro,
        //     observaciones: a.signosVitales?.observaciones,
        //   })),
        // );

        // El backend ya devuelve únicamente
        // las atenciones de este paciente
        this.atenciones = atenciones;

        this.cargando = false;

        // Mostrar presión al abrir
        this.cambiarGrafica('presion');
      },

      error: (error) => {
        // console.error('========== ERROR AL OBTENER ATENCIONES ==========');

        // console.error(error);

        this.atenciones = [];
        this.cargando = false;
        this.series = [];
      },
    });
  }

  // =====================================================
  // OBTENER SIGNOS VITALES DE LAS ATENCIONES
  // =====================================================

  get signosVitales(): SignosVitales[] {
    return this.atenciones

      .filter((atencion) => atencion.signosVitales != null)

      .map((atencion) => atencion.signosVitales!)

      .sort((a, b) => new Date(a.fechaRegistro).getTime() - new Date(b.fechaRegistro).getTime());
  }

  get ultimaObservacion(): string | null {
    if (!this.signosVitales.length) {
      return null;
    }

    // signosVitales ya está ordenado del más antiguo al más reciente
    const ultimoRegistro = this.signosVitales[this.signosVitales.length - 1];

    const observacion = ultimoRegistro.observaciones?.trim();

    // Si el último registro no tiene observación,
    // no buscamos una observación anterior.
    if (!observacion) {
      return null;
    }

    // Ignorar el mensaje automático del triage
    if (observacion.toLowerCase() === 'signos vitales registrados durante el triage') {
      return null;
    }

    return observacion;
  }

  // =====================================================
  // CAMBIAR GRÁFICA
  // =====================================================

  cambiarGrafica(tipo: string): void {
    // ===================================================
    // PESO
    // ===================================================

    if (tipo === 'peso') {
      this.titulo = 'Peso';

      this.unidad = 'kg';

      this.colores = ['#2F6F73'];

      this.series = [
        {
          name: 'Peso',

          data: this.signosVitales

            .filter((s) => s.peso != null)

            .map((s) => [new Date(s.fechaRegistro).getTime(), s.peso!]),
        },
      ];
    }

    // ===================================================
    // GLUCOSA
    // ===================================================
    else if (tipo === 'glucosa') {
      this.titulo = 'Glucosa';

      this.unidad = 'mg/dL';

      this.colores = ['#43A047'];

      this.series = [
        {
          name: 'Glucosa',

          data: this.signosVitales

            .filter((s) => s.glucosa != null)

            .map((s) => [new Date(s.fechaRegistro).getTime(), s.glucosa!]),
        },
      ];
    }

    // ===================================================
    // PRESIÓN ARTERIAL
    // ===================================================
    else if (tipo === 'presion') {
      this.titulo = 'Presión arterial';

      this.unidad = 'mmHg';

      this.colores = ['#D32F2F', '#1976D2'];

      // ===============================================
      // REGISTROS CON PRESIÓN
      // ===============================================

      const registrosPresion = this.signosVitales.filter(
        (s) => s.presionSistolica != null || s.presionDiastolica != null,
      );

      // ===============================================
      // CREAR SERIES
      // ===============================================

      this.series = [
        // =============================================
        // SISTÓLICA
        // =============================================

        {
          name: 'Sistólica',

          data: registrosPresion

            .filter((s) => s.presionSistolica != null)

            .map((s) => [new Date(s.fechaRegistro).getTime(), s.presionSistolica!]),
        },

        // =============================================
        // DIASTÓLICA
        // =============================================

        {
          name: 'Diastólica',

          data: registrosPresion

            .filter((s) => s.presionDiastolica != null)

            .map((s) => [new Date(s.fechaRegistro).getTime(), s.presionDiastolica!]),
        },
      ];
    }

    // console.log('========== SERIES ==========');

    // console.log(this.series);
  }

  hayDatosGrafica(): boolean {
    if (!this.series || this.series.length === 0) {
      return false;
    }

    return this.series.some((serie) => {
      return Array.isArray(serie.data) && serie.data.length > 0;
    });
  }

  // =====================================================
  // CERRAR
  // =====================================================

  cerrar(): void {
    this.dialogRef.close();
  }

  // =====================================================
  // ABRIR REGISTRO DE SIGNOS VITALES
  // =====================================================

  abrirRegistroSignosVitales(): void {
    if (!this.paciente?.id) {
      // console.error('No se encontró el ID del paciente');

      return;
    }

    this.atencionService.obtenerAtenciones(this.paciente.id).subscribe({
      next: (atenciones) => {
        // console.log('Atenciones del paciente:', atenciones);

        const atencionActiva = atenciones.find(
          (atencion) => atencion.estado === 'EN_ESPERA' || atencion.estado === 'EN_ATENCION',
        );

        if (atencionActiva) {
          if (atencionActiva.estado === 'EN_ESPERA') {
            Swal.fire({
              icon: 'info',
              title: 'Paciente en sala de espera',
              text: `${this.paciente.nombre} ${this.paciente.apellido ?? ''} ya se encuentra en sala de espera.`,
              showConfirmButton: false,
              timer: 2000,
            });
          } else {
            Swal.fire({
              icon: 'info',
              title: 'Paciente en consulta',
              text: `${this.paciente.nombre} ${this.paciente.apellido ?? ''} ya tiene una atención en curso.`,
              showConfirmButton: false,
              timer: 2000,
            });
          }

          return;
        }

        this.dialogRef.close();

        this.dialog.open(RegistroSignosVitales, {
          width: '95vw',
          maxWidth: '95vw',
          height: '90vh',
          data: this.paciente,
        });
      },

      error: (error) => {
        // console.error('Error al verificar las atenciones del paciente:', error);

        Swal.fire({
          icon: 'error',
          title: 'No se pudo verificar al paciente',
          text: 'No fue posible comprobar si el paciente tiene una atención activa.',
          showConfirmButton: false,
          timer: 2000,
        });
      },
    });
  }
}
